/**
 * Data layer for `top-advertisers-widget`. Thin typed fetch wrappers over /api/v1/top-advertisers-widget.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export type TopN = 5 | 10 | 25;

export interface TopAdvertiserRow {
	id: string;
	rank: number;
	advertiser: string;
	spend: number;
	conversions: number;
	cr: number;
	roas: number;
	trend: number[];
	[key: string]: unknown;
}

export interface ListTopAdvertisersWidgetResponse {
	count: number;
	items: TopAdvertiserRow[];
}

export interface ListTopAdvertisersWidgetParams {
	from?: string;
	to?: string;
	top?: TopN;
}

const BASE = "/api/v1/top-advertisers-widget";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(
	params: ListTopAdvertisersWidgetParams = {},
): Promise<ListTopAdvertisersWidgetResponse> {
	const qs = new URLSearchParams();
	if (params.from) qs.set("from", params.from);
	if (params.to) qs.set("to", params.to);
	if (params.top) qs.set("top", String(params.top));
	const suffix = qs.toString();
	const url = suffix ? `${BASE}?${suffix}` : BASE;
	const res = await fetch(url, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListTopAdvertisersWidgetResponse>(res);
}

export const topAdvertisersWidgetQueryKeys = {
	all: ["top-advertisers-widget"] as const,
	list: (params: ListTopAdvertisersWidgetParams) =>
		[...topAdvertisersWidgetQueryKeys.all, params] as const,
};
